import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { glassSurfaceClassName } from './glass.ts'

/**
 * Dialog — a shared modal overlay on the Radix base (`@radix-ui/react-dialog`):
 * focus trap, esc-to-close, `role="dialog"` labelled by its `DialogTitle`, and
 * an outside-click / `DialogClose` dismissal. Compose: `Dialog` (root, controlled
 * via `open`/`onOpenChange` or uncontrolled via `defaultOpen`) wrapping a
 * `DialogTrigger`, an optional `DialogOverlay`, and a `DialogContent` holding a
 * `DialogHeader` (`DialogTitle` + optional `DialogDescription`) and a
 * `DialogFooter` with its actions, each action usually a `DialogClose`.
 *
 * Styled with design tokens only: the content is a glass panel
 * (`bg-surface-glass` with the `border-surface-glass-border` edge and the
 * large brand shadow); the overlay is the `bg-overlay` sheet, blurred. Like
 * the other overlay primitives (Tooltip), the content is rendered in place
 * rather than through a `Portal`, so the live `/styleguide` route can snapshot
 * it (the snapshot reads each specimen's own DOM subtree).
 *
 * ADR 023 removed *route-based* dialogs in favour of pages — this primitive is
 * the deliberate exception for transient **client overlays** (a ⌘K palette, a
 * confirm step): not content-bearing or bookmarkable, so it has no business
 * being a route. Reach for a page, not this, whenever the content should be
 * linkable or survive a refresh.
 */
function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger(
	props: React.ComponentProps<typeof DialogPrimitive.Trigger>,
) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogClose(
	props: React.ComponentProps<typeof DialogPrimitive.Close>,
) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

/**
 * The overlay sheet. Radix renders `DialogOverlay` only for a modal dialog, so
 * the contained, non-modal `/styleguide` specimen paints its sheet with these
 * classes on a plain element.
 */
const dialogOverlayClassName =
	'bg-overlay fixed inset-0 z-50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'

function DialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn(dialogOverlayClassName, className)}
			{...props}
		/>
	)
}

function DialogContent({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
	return (
		<DialogPrimitive.Content
			data-slot="dialog-content"
			className={cn(
				glassSurfaceClassName,
				'text-popover-foreground shadow-brand-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-4 max-md:w-[calc(100%-2rem)]',
				className,
			)}
			{...props}
		/>
	)
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn('flex flex-col gap-1', className)}
			{...props}
		/>
	)
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn('flex flex-wrap justify-end gap-2', className)}
			{...props}
		/>
	)
}

function DialogTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn('text-h6 leading-none font-semibold', className)}
			{...props}
		/>
	)
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn('text-muted-foreground text-body-xs', className)}
			{...props}
		/>
	)
}

export {
	dialogOverlayClassName,
	Dialog,
	DialogTrigger,
	DialogClose,
	DialogOverlay,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
}
